import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormControl, FormRecord, FormsModule, ReactiveFormsModule } from '@angular/forms';

import { Mapper, NgUtils, TiimePipesModule } from 'tiime-expert-utils';
import { TiimeChipInputEvent, TiimeChipsModule, TiimeIconModule, TiimeInputContainerModule } from 'tiime-material';

import { EmailAddressForm } from '@forms/email-address-form';
import { ValidatorHelper } from '@helpers/validators.helper';
import { ErrorMessage } from '@interfaces/error-message';

@Component({
  standalone: true,
  imports: [
    TiimeInputContainerModule,
    FormsModule,
    ReactiveFormsModule,
    TiimeChipsModule,
    TiimeIconModule,
    TiimePipesModule
  ],
  selector: 'app-email-address-form',
  templateUrl: './email-address-form.component.html',
  styleUrls: ['./email-address-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EmailAddressFormComponent implements OnInit, OnDestroy {
  @Input({ required: true }) label: string;
  @Input() required = false;
  @Input() form: FormArray<EmailAddressForm>;
  @Input() errorMessage: ErrorMessage | null;

  readonly emailCtrl: FormControl<string> = new FormControl<string>('', {
    nonNullable: true,
    validators: [ValidatorHelper.emailValidator()]
  });

  get emailFields(): FormRecord | null {
    return this.form?.root.get('emailFields') as FormRecord | null;
  }

  inputFocused = false;

  protected readonly separatorKeysCodes = [ENTER, COMMA];
  protected readonly trackByIndex = NgUtils.trackByIndex;

  protected mapToLabel: Mapper<string, string> = (label: string) => (this.required ? `${label}*` : label);

  ngOnInit(): void {
    if (this.emailFields) {
      this.emailFields.addControl(this.label, this.emailCtrl);
    }
  }

  onFocus(): void {
    this.inputFocused = true;
  }

  onBlur(): void {
    this.form.markAsTouched();
    this.inputFocused = false;
  }

  addEmail(event: TiimeChipInputEvent): void {
    this.emailCtrl.markAsTouched();
    if (this.emailCtrl.invalid) {
      return;
    }

    const value = (event.value || '').trim();
    if (value) {
      this.form.push(this.createEmailAddressForm(value));
    }
    // clear
    event.input.value = '';
  }

  removeEmail(index: number): void {
    this.form.removeAt(index);
  }

  private createEmailAddressForm(email: string): EmailAddressForm {
    const emailAddressForm = new EmailAddressForm();
    emailAddressForm.patchValue({
      address: email,
      canBeDeleted: true
    });
    return emailAddressForm;
  }

  ngOnDestroy(): void {
    if (this.emailFields) {
      this.emailFields.removeControl(this.label);
    }
  }
}
